/**
 * labelLoadRetry — bounded retry for the boot label load, mirroring firehoseRetry.
 *
 * The boot getLapLabels is a single bridge call subject to the 5s bridge timeout.
 * A slow first read during the first-launch auth window used to set labelsLoadFailed
 * and block every correction for the session (C2a). This retries with backoff so a
 * transient slow read recovers. A missing bridge (no-handler) never resolves, so we
 * do NOT retry it — we report failed immediately. Pure + injectable sleep so it is
 * unit-tested.
 */
import type { LapLabel } from "@contract/types";
import { BridgeUnavailableError } from "../bridge/client";

const DEFAULT_BACKOFFS_MS = [800, 1600, 3200];

export interface LabelLoadRetryOptions {
  backoffsMs?: number[];
  sleep?: (ms: number) => Promise<void>;
  isCancelled?: () => boolean;
}

export async function fetchLabelsWithRetry(
  fetchOnce: () => Promise<LapLabel[]>,
  opts: LabelLoadRetryOptions = {},
): Promise<{ labels: LapLabel[]; failed: boolean }> {
  const backoffs = opts.backoffsMs ?? DEFAULT_BACKOFFS_MS;
  const sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
  const cancelled = opts.isCancelled ?? (() => false);

  for (let attempt = 0; attempt <= backoffs.length; attempt++) {
    try {
      return { labels: await fetchOnce(), failed: false };
    } catch (err) {
      // A missing bridge (desktop preview / handler gone) will never resolve —
      // do not spin. Report failed so the caller blocks writes (C2a) rather than
      // silently writing over labels it never read.
      if (err instanceof BridgeUnavailableError && err.reason === "no-handler") {
        return { labels: [], failed: true };
      }
    }
    if (cancelled()) return { labels: [], failed: true };
    const backoff = backoffs[attempt];
    if (backoff === undefined) break; // retries exhausted
    await sleep(backoff);
    if (cancelled()) return { labels: [], failed: true };
  }
  return { labels: [], failed: true };
}
